"use client"
import css from "@css/modules/DealScroeDialog.module.scss"
import { Listing, TopListing } from "@t/Listing"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import Image from "next/image"
import DealScoreImg from "@public/images/dealScoreLogo.png"
import InfoImg from "@public/images/Info.png"
import { StartRating } from "@components/StartRating"
import { getPointsText } from "@components/Listings/DealScore/utils"
import { getPointMinMax } from "@utils/listingUrl"
import { DealScoreTable } from "@components/Listings/DealScore/DealScoreTable"
import { CloseIcon } from "@css/icons/CloseIcon"
import StealIcon from "./icons/Fast Track.png"
import LeaderIcon from "./icons/Leaderboard.png"
import RankIcon from "./icons/Rank.png"
import { StatItem } from "@components/Listings/DealScoreDialog/StatItem"
import { UsablePointsStatItem } from "@components/Listings/DealScoreDialog/UsablePointsStatItem"
import { OldKeyWestStats } from "@components/Listings/DealScoreDialog/OldKeyWestStats"
import { addNumberPostfix } from "@utils/number"

export type DealScoreDialogProps = { className?: string; listing: Listing; topListings: TopListing[]; total: number }
export default function DealScoreDialogComponent({ listing, topListings, total }: DealScoreDialogProps) {
    const [open, setOpen] = useState(false)
    const elemRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        elemRef.current = document.createElement("div")
        document.getElementById("dialog-portal")?.appendChild(elemRef.current)

        return () => {
            document.getElementById("dialog-portal")?.removeChild(elemRef.current)
        }
    }, [])
    const { pointsMin, pointsMax } = useMemo(() => getPointMinMax(listing), [listing.points_on_contract])
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = ""
        }
    }, [open])

    const onOpen = () => {
        setOpen(true)
    }
    const onClose = () => {
        setOpen(false)
    }
    const portal =
        open &&
        createPortal(
            <div className={css.dialogRoot}>
                <div className={css.backDrop} onClick={onClose} />
                <div className={css.dialogContent}>
                    <button onClick={onClose} className={css.closeBtn}>
                        <CloseIcon />
                    </button>
                    <div className={css.left}>
                        <Image className={css.dealScoreLogo} quality={90} src={DealScoreImg} alt="Deal score" width={192} height={37} />
                        <StartRating rating={listing.stars || 1} size={26} gap={9.75} className={css.starRating} />
                        <div className={css.dealScoreDesc}>
                            <span>{listing.resort.short_name} listings with</span>&nbsp;&nbsp;
                            <span className={css.purpleText}>{getPointsText(pointsMin, pointsMax)}.</span>
                        </div>
                        <div className={css.tableWrapper}>
                            <DealScoreTable listing={listing} topListings={topListings} title={`${listing.resort.short_name} Listings`} />
                        </div>
                        <div className={css.infoText}>
                            <Image src={InfoImg} alt="info icon" width={20} height={20} />
                            &nbsp;Rankings are refreshed multiple times per day
                        </div>
                    </div>
                    <div className={css.right}>
                        <h3>Deal Report : Listing #{listing.id}</h3>
                        {listing.is_steal && (
                            <StatItem image={<Image src={StealIcon} alt="It’s a Steal!" width={40} height={40} />} imgText="It’s a Steal!">
                                <strong>This listing is a steal! </strong> A &quot;steal&quot; is an exceptional deal that stands out statistically. It falls outside the typical
                                range of our listings, indicating a significantly better value than average.
                            </StatItem>
                        )}
                        <StatItem image={<Image src={RankIcon} alt="Rank" width={40} height={40} />} imgText="Rank">
                            Among its category peers, this listing has an{" "}
                            <strong>
                                overall Deal Score rank of {addNumberPostfix(listing.rank)} out of {total}
                            </strong>
                            . Data from thousands of current and historic DVC resale listings were used in this evaluation.
                        </StatItem>
                        <StatItem image={<Image src={LeaderIcon} alt="$/pt Rank" width={40} height={40} />} imgText="$/pt Rank">
                            This listing{" "}
                            <strong>
                                ranks {addNumberPostfix(listing.price_per_point_rank)} of {total} in “Price-Per-Point”
                            </strong>
                            , an important metric, yet it&apos;s just part of the story - The DealScore is a better overall predictor of value as it takes all key metrics into
                            account, including price trends for recently sold listings.
                        </StatItem>
                        <UsablePointsStatItem listing={listing} />
                        <OldKeyWestStats listing={listing} />
                    </div>
                </div>
            </div>,
            elemRef.current
        )
    return (
        <>
            <div className={css.btnWrapper}>
                <button onClick={onOpen} className={css.openBtn}>
                    Read The Full Report {">"}
                </button>
            </div>
            {portal}
        </>
    )
}
